import React, { useEffect, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { useAppSelector, useAppDispatch } from '@/lib/hooks';
import { updateMapStyle } from '@/features/user/userSlice';
import { loadSettings } from '@/lib/utils';
import { useTheme } from '@/lib/ThemeProvider';
import { getCountries } from '@/api/apiService';
import { Marker as MarkerType } from '@/features/guides/guidesSlice';
import defaultStyle from '@/data/map/styles/default.json';
import darkStyle from '@/data/map/styles/dark.json';
import lightStyle from '@/data/map/styles/light.json';
import {getPlaceMapLink} from "@/helpers.ts";

const mapStyles = {
  theme_color: defaultStyle,
  default: defaultStyle,
  dark: darkStyle,
  light: lightStyle
};

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '0.5rem'
};

const countryCoordinates: Record<string, { lat: number, lng: number, zoom: number }> = {};

const defaultCenter = {
  lat: 20,
  lng: 0,
  zoom: 12,
};

interface MapProps {
  className?: string;
  selectedCountry?: string;
  countryCode?: string;
  markers?: MarkerType[];
}

const getCountryCoordinates = async (countryName?: string, countryCode?: string) => {
  if (countryCode) {
    try {
      const countries = await getCountries();
      if (countries) {
        const country = countries.find((c: any) => c.isoCode === countryCode);
        if (country) {
          if (countryCoordinates[country.name]) {
            return countryCoordinates[country.name];
          }

          if (country.latitude && country.longitude) {
            return {
              lat: parseFloat(country.latitude),
              lng: parseFloat(country.longitude),
              zoom: 5
            };
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch countries for coordinates:', error);
    }
  }

  if (countryName && countryCoordinates[countryName]) {
    return countryCoordinates[countryName];
  }

  return defaultCenter;
};

const Map = ({ className, selectedCountry, countryCode, markers }: MapProps) => {
  const mapStyle = useAppSelector(state => state.user.preferences.mapStyle);
  const { isDark } = useTheme();
  const dispatch = useAppDispatch();
  const [selectedMarker, setSelectedMarker] = useState<MarkerType | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [hasFittedBounds, setHasFittedBounds] = useState(false);

  useEffect(() => {
    const settings = loadSettings();
    if (settings.mapStyle) {
      dispatch(updateMapStyle(settings.mapStyle));
    }
  }, [dispatch]);

  useEffect(() => {
    setHasFittedBounds(false);
  }, [markers]);

  useEffect(() => {
    if (map && markers && markers.length > 0 && !hasFittedBounds) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(marker => {
        bounds.extend({
          lat: parseFloat(marker.latitude),
          lng: parseFloat(marker.longitude)
        });
      });

      const padding = { top: 80, bottom: 80, left: 80, right: 80 };
      map.fitBounds(bounds, padding);
      setHasFittedBounds(true);
    }
  }, [map, markers, hasFittedBounds]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(2);

  useEffect(() => {
    const updateMapCenterAndZoom = async () => {
      if (markers && markers.length > 0) {
        setMapCenter(defaultCenter);
        setMapZoom(2);
        return;
      }

      const coordinates = await getCountryCoordinates(selectedCountry, countryCode);
      const center = coordinates || defaultCenter;
      const zoom = coordinates !== defaultCenter ? coordinates?.zoom : 2;
      setMapCenter(center);
      setMapZoom(zoom);
    };

    updateMapCenterAndZoom();
  }, [selectedCountry, countryCode, markers]);


  const getMapStyle = () => {
    if (mapStyle === 'theme_color') {
      return isDark ? mapStyles.dark : mapStyles.light;
    }
    return mapStyles[mapStyle as keyof typeof mapStyles] || mapStyles.default;
  };

  const options = {
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: true,
    fullscreenControl: true,
    styles: getMapStyle(),
  };

  return (
    <div className={className}>
      {!isLoaded ? (
        <div className="w-full h-full flex items-center justify-center bg-accent/10">
          <p className="text-muted-foreground">Loading map...</p>
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          options={options}
          onLoad={setMap}
        >
          {markers && markers.map((marker, index) => (
            <Marker
              key={index}
              position={{
                lat: parseFloat(marker.latitude),
                lng: parseFloat(marker.longitude)
              }}
              onClick={() => setSelectedMarker(marker)}
              icon={{
                url: '/pins/marker-icon.png',
                scaledSize: new window.google.maps.Size(30, 52),
                anchor: new window.google.maps.Point(22, 38),
              }}
            />
          ))}

          {selectedMarker && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedMarker.latitude),
                lng: parseFloat(selectedMarker.longitude)
              }}
              onCloseClick={() => setSelectedMarker(null)}
              options={{
                pixelOffset: new window.google.maps.Size(-5, -40)
              }}
            >
              <div className="p-2 max-w-xs">
                <h3 className="font-semibold text-black text-lg mb-2">{selectedMarker.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{selectedMarker.description}</p>
                {selectedMarker.long_description && (
                  <p className="text-xs text-gray-500 mb-2">{selectedMarker.long_description}</p>
                )}
                <div className="flex justify-between items-center text-xs text-gray-500">
                  {/*{selectedMarker.time_to_arrive && (*/}
                  {/*  <span>⏱️ {selectedMarker.time_to_arrive}</span>*/}
                  {/*)}*/}
                  {/*{selectedMarker.price && (*/}
                  {/*  <span>💰 {selectedMarker.price}</span>*/}
                  {/*)}*/}
                </div>
                {selectedMarker.name && (
                  <a
                      rel="noopener noreferrer"
                      className="mt-2 block text-black"
                      href={ getPlaceMapLink( `${selectedMarker.name},${selectedCountry}` )}
                      target="_blank">
                    🗺️ Open in maps
                  </a>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </div>
  );
};

export default Map;
